import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Printer, Cpu, Sparkles, Gamepad2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import HeroSection from '@/components/HeroSection';
import FigurineGrid from '@/components/FigurineGrid';
import AuthModal from '@/components/AuthModal';

const GAMES = [
  {
    id: 'skeleton',
    label: 'GRA #1',
    title: 'Przygody Małego Szkieletora',
    desc: 'Zbieraj złote figurki, depczaj szkielety, omijaj duchy. Zebrane figurki trafią na Twoje konto!',
    emoji: '💀',
    accentFrom: '#0e0820',
    accentVia: '#180b38',
    accentTo: '#0e0820',
    border: 'border-purple-700/40',
    shadow: 'rgba(120,60,255,0.18)',
    iconBg: 'bg-purple-900/60',
    iconBorder: 'border-purple-600/50',
    iconColor: 'text-purple-300',
    labelColor: 'text-purple-400',
    glowL: 'from-purple-900/20',
    glowR: 'from-yellow-900/10',
    btnFrom: 'from-yellow-400',
    btnTo: 'to-yellow-500',
    btnHoverFrom: 'hover:from-yellow-300',
    btnHoverTo: 'hover:to-yellow-400',
    btnShadow: 'rgba(240,192,32,0.4)',
    btnHoverShadow: 'rgba(240,192,32,0.6)',
    btnText: 'text-black',
    route: '/game',
  },
  {
    id: 'venom',
    label: 'GRA #2',
    title: 'Venom: Atak Symbionty',
    desc: 'Biegaj, skacz i wystrzeliwuj językiem by zbierać figurki i zjadać wrogów! Zdobądź jak najwięcej punktów!',
    emoji: '🖤',
    accentFrom: '#0a0014',
    accentVia: '#1a0628',
    accentTo: '#0a0014',
    border: 'border-red-900/40',
    shadow: 'rgba(200,0,60,0.16)',
    iconBg: 'bg-red-950/60',
    iconBorder: 'border-red-700/50',
    iconColor: 'text-red-400',
    labelColor: 'text-red-400',
    glowL: 'from-red-900/20',
    glowR: 'from-purple-900/10',
    btnFrom: 'from-red-500',
    btnTo: 'to-red-700',
    btnHoverFrom: 'hover:from-red-400',
    btnHoverTo: 'hover:to-red-600',
    btnShadow: 'rgba(200,0,60,0.4)',
    btnHoverShadow: 'rgba(200,0,60,0.6)',
    btnText: 'text-white',
    route: '/venom',
  },
] as const;

interface HomeProps {
  onAuthOpen: (mode: 'login' | 'register') => void;
  authOpen: boolean;
  authMode: 'login' | 'register';
  onAuthClose: () => void;
  onAuthModeChange: (mode: 'login' | 'register') => void;
}

export default function Home({ onAuthOpen, authOpen, authMode, onAuthClose, onAuthModeChange }: HomeProps) {
  const collectionRef = useRef<HTMLDivElement>(null);
  const [activeGame, setActiveGame] = useState(0);

  const scrollToCollection = () => {
    collectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const prevGame = () => setActiveGame(i => (i - 1 + GAMES.length) % GAMES.length);
  const nextGame = () => setActiveGame(i => (i + 1) % GAMES.length);

  const g = GAMES[activeGame];

  return (
    <main>
      {/* Hero */}
      <HeroSection onExplore={scrollToCollection} />

      {/* GAME CAROUSEL */}
      <section className="py-10 px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <Gamepad2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-bold tracking-widest text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              MINI-GRY PRZEGLĄDARKOWE
            </span>
          </div>

          {/* Carousel wrapper */}
          <div className="relative flex items-center gap-2">
            {/* Left arrow */}
            <button
              onClick={prevGame}
              className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-white/60 hover:text-white"
              aria-label="Poprzednia gra"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Card */}
            <div className="flex-1 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={g.id}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.22 }}
                >
                  <div
                    className={`relative rounded-2xl overflow-hidden border ${g.border} px-6 py-6 flex flex-col sm:flex-row items-center gap-5`}
                    style={{
                      background: `linear-gradient(to right, ${g.accentFrom}, ${g.accentVia}, ${g.accentTo})`,
                      boxShadow: `0 0 40px ${g.shadow}`,
                    }}
                  >
                    {/* Glow accents */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className={`absolute top-0 left-0 w-56 h-full bg-gradient-to-r ${g.glowL} to-transparent`} />
                      <div className={`absolute top-0 right-0 w-56 h-full bg-gradient-to-l ${g.glowR} to-transparent`} />
                    </div>

                    {/* Icon + text */}
                    <div className="relative flex items-center gap-4 flex-1 min-w-0">
                      <div className={`w-14 h-14 rounded-xl ${g.iconBg} border ${g.iconBorder} flex items-center justify-center shrink-0 text-2xl`}>
                        {g.emoji}
                      </div>
                      <div className="min-w-0">
                        <p className={`text-xs font-bold tracking-widest ${g.labelColor} mb-1`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {g.label} — MINI-GRA PRZEGLĄDARKOWA
                        </p>
                        <p className="text-base font-black text-white leading-tight truncate" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                          {g.title}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                          {g.desc}
                        </p>
                      </div>
                    </div>

                    {/* CTA */}
                    <Link
                      to={g.route}
                      className={`relative shrink-0 px-6 py-3 rounded-xl font-black text-sm tracking-widest ${g.btnText} bg-gradient-to-r ${g.btnFrom} ${g.btnTo} ${g.btnHoverFrom} ${g.btnHoverTo} transition-all`}
                      style={{
                        fontFamily: 'Orbitron, sans-serif',
                        boxShadow: `0 0 20px ${g.btnShadow}`,
                      }}
                    >
                      🎮 ZAGRAJ
                    </Link>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right arrow */}
            <button
              onClick={nextGame}
              className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-white/60 hover:text-white"
              aria-label="Następna gra"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-4">
            {GAMES.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveGame(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === activeGame ? 'bg-white scale-125' : 'bg-white/25 hover:bg-white/50'}`}
                aria-label={`Gra ${i + 1}`}
              />
            ))}
          </div>
        </motion.div>
      </section>

      {/* PERSONALIZOWANE */}
      <section className="pb-4 px-4">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto"
        >
          <Link
            to="/personalizacja"
            className="group relative flex flex-col sm:flex-row items-center gap-5 rounded-2xl overflow-hidden border border-cyan-800/40 px-6 py-6 transition-all hover:border-cyan-500/60 no-underline"
            style={{
              background: 'linear-gradient(to right, #030c12, #06141f, #030c12)',
              boxShadow: '0 0 36px rgba(0,190,210,0.09)',
            }}
          >
            {/* Glow accents */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 left-0 w-56 h-full bg-gradient-to-r from-cyan-900/20 to-transparent" />
              <div className="absolute top-0 right-0 w-56 h-full bg-gradient-to-l from-teal-900/10 to-transparent" />
            </div>
            {/* Icon + text */}
            <div className="relative flex items-center gap-4 flex-1 min-w-0">
              <div className="w-14 h-14 rounded-xl bg-cyan-950/60 border border-cyan-700/40 flex items-center justify-center shrink-0 text-2xl group-hover:border-cyan-500/60 transition-colors">
                ✨
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold tracking-widest text-cyan-400 mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  PROJEKT NA ZAMÓWIENIE
                </p>
                <p className="text-base font-black text-white leading-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  PERSONALIZOWANE
                </p>
                <p className="text-sm text-muted-foreground mt-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  Wyślij zdjęcia referencyjne i opisz projekt — zaprojektujemy Twoją unikalną figurkę.
                </p>
              </div>
            </div>
            {/* CTA */}
            <div
              className="relative shrink-0 px-6 py-3 rounded-xl font-black text-sm tracking-widest text-white transition-all group-hover:shadow-[0_0_24px_rgba(0,200,220,0.45)]"
              style={{
                fontFamily: 'Orbitron, sans-serif',
                background: 'linear-gradient(135deg, #0d7a8c, #095f70)',
                boxShadow: '0 0 18px rgba(0,190,210,0.28)',
              }}
            >
              Zamów projekt →
            </div>
          </Link>
        </motion.div>
      </section>
      <section className="py-20 px-4 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span
            className="text-xs font-bold tracking-widest text-muted-foreground"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            PROCES
          </span>
          <h2
            className="text-3xl sm:text-4xl font-black text-foreground mt-2"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            JAK TO DZIAŁA?
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: <Sparkles className="w-8 h-8" />,
              step: '01',
              title: 'AI DESIGN',
              desc: 'Koncepcja figurki projektowana jest przy użyciu narzędzi AI — każdy detal jest przemyślany.',
              color: 'var(--primary)',
            },
            {
              icon: <Cpu className="w-8 h-8" />,
              step: '02',
              title: 'MESHY 3D',
              desc: 'Model 3D jest tworzony i teksturowany w Meshy AI z zachowaniem najdrobniejszych szczegółów.',
              color: 'var(--accent)',
            },
            {
              icon: <Printer className="w-8 h-8" />,
              step: '03',
              title: 'DRUK FDM',
              desc: 'Figurka jest drukowana z precyzją 0.1mm i ręcznie wykańczana przed wysyłką.',
              color: 'var(--chart-4)',
            },
          ].map((item, i) => (
            <motion.div
              key={item.step}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.6 }}
              className="relative rounded-2xl border p-8"
              style={{
                background: 'oklch(0.16 0.03 280)',
                borderColor: 'oklch(0.25 0.045 280)',
                boxShadow: `0 4px 32px -8px ${item.color}22`,
              }}
            >
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                style={{ background: `${item.color}18`, color: item.color, border: `1px solid ${item.color}40` }}
              >
                {item.icon}
              </div>
              <span
                className="text-4xl font-black opacity-10 absolute top-6 right-6"
                style={{ fontFamily: 'Orbitron, sans-serif', color: item.color }}
              >
                {item.step}
              </span>
              <h3
                className="text-base font-black mb-3 tracking-wider"
                style={{ fontFamily: 'Orbitron, sans-serif', color: item.color }}
              >
                {item.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Collection */}
      <div ref={collectionRef}>
        <FigurineGrid onAuthRequired={() => onAuthOpen('login')} />
      </div>

      {/* CTA Banner */}
      <section className="py-20 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto rounded-3xl border p-12 text-center relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, oklch(0.18 0.04 280), oklch(0.15 0.03 195))',
            borderColor: 'oklch(0.30 0.06 280)',
            boxShadow: '0 0 60px oklch(0.65 0.32 280 / 0.12)',
          }}
        >
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage: `linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
            opacity: 0.12,
          }} />
          <div className="relative z-10">
            <span
              className="text-xs font-bold tracking-widest text-primary mb-4 block"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              PERSONALIZACJA
            </span>
            <h2
              className="text-3xl sm:text-5xl font-black text-foreground mb-4"
              style={{ fontFamily: 'Orbitron, sans-serif' }}
            >
              WŁASNA FIGURKA?
            </h2>
            <p
              className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              Opisz nam swój pomysł — my zaprojektujemy i wydrukujemy figurkę według Twoich wymagań.
            </p>
            <Link
              to="/personalizacja"
              className="inline-block px-10 py-4 rounded-xl font-black tracking-widest text-white text-center"
              style={{
                fontFamily: 'Orbitron, sans-serif',
                fontSize: '0.8rem',
                background: 'linear-gradient(135deg, var(--primary), var(--accent))',
                boxShadow: '0 0 32px oklch(0.65 0.32 280 / 0.4)',
              }}
            >
              ZAMÓW PROJEKT
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Auth Modal */}
      <AuthModal
        open={authOpen}
        mode={authMode}
        onClose={onAuthClose}
        onModeChange={onAuthModeChange}
      />
    </main>
  );
}
